import dayjs from 'dayjs'
import { createSignal, createMemo, createEffect, onMount, For, batch } from 'solid-js'
import { Portal } from 'solid-js/web'
import { AddOutline, CreateOutline, SearchOutline, TrashOutline, EyeOutline, CopyLine, Refresh3Line, ArrowUpOutline, ArrowDownOutline, CheckmarkCircleOutline, CloseCircleOutline } from '@/icons'
import { res2json, expiresDays } from '@/utils'
import { paginator } from '@lib/utils'
import { addToast } from '@/components/Toast'
import { store } from '@/store'
import * as constants from '@lib/constants'
import * as games from '@games'

const [result, setResult] = createSignal({})

export default function Agents() {
  const query = new URLSearchParams(document.location.search)
  const [game, setGame] = createSignal(query.get('game') ?? '')
  const [keywords, setKeywords] = createSignal(query.get('k') ?? '')
  const [editAgent, setEditAgent] = createSignal({})
  const [selected, setSelected] = createSignal([])
  const [sort, setSort] = createSignal('id-down')

  function getSortedRows(order) {
    const rows = result().rows ?? []
    switch (order) {
      case 'id-up':
        rows.sort((a, b) => a.id === b.id ? 0 : a.id > b.id ? 1 : -1)
        break
      case 'id-down':
        rows.sort((a, b) => a.id === b.id ? 0 : a.id < b.id ? 1 : -1)
        break
      case 'username-up':
        rows.sort((a, b) => a.username === b.username ? 0 : a.username > b.username ? 1 : -1)
        break
      case 'username-down':
        rows.sort((a, b) => a.username === b.username ? 0 : a.username < b.username ? 1 : -1)
        break
      case 'nickname-up':
        rows.sort((a, b) => a.nickname === b.nickname ? 0 : a.nickname > b.nickname ? 1 : -1)
        break
      case 'nickname-down':
        rows.sort((a, b) => a.nickname === b.nickname ? 0 : a.nickname < b.nickname ? 1 : -1)
        break
      case 'game-up':
        rows.sort((a, b) => a.games[0].game === b.games[0].game ? 0 : a.games[0].game > b.games[0].game ? 1 : -1)
        break
      case 'game-down':
        rows.sort((a, b) => a.games[0].game === b.games[0].game ? 0 : a.games[0].game < b.games[0].game ? 1 : -1)
        break
      case 'status-up':
        rows.sort((a, b) => a.is_disabled === b.is_disabled ? 0 : a.is_disabled > b.is_disabled ? 1 : -1)
        break
      case 'status-down':
        rows.sort((a, b) => a.is_disabled === b.is_disabled ? 0 : a.is_disabled < b.is_disabled ? 1 : -1)
        break
      case 'expires-up':
        rows.sort((a, b) => a.games[0].expires_at === b.games[0].expires_at ? 0 : a.games[0].expires_at > b.games[0].expires_at ? 1 : -1)
        break
      case 'expires-down':
        rows.sort((a, b) => a.games[0].expires_at === b.games[0].expires_at ? 0 : a.games[0].expires_at < b.games[0].expires_at ? 1 : -1)
        break
      case 'created-up':
        rows.sort((a, b) => a.created_at === b.created_at ? 0 : a.created_at > b.created_at ? 1 : -1)
        break
      case 'created-down':
        rows.sort((a, b) => a.created_at === b.created_at ? 0 : a.created_at < b.created_at ? 1 : -1)
        break
      case 'login-up':
        rows.sort((a, b) => a.last_logged_in_at === b.last_logged_in_at ? 0 : a.last_logged_in_at > b.last_logged_in_at ? 1 : -1)
        break
      case 'login-down':
        rows.sort((a, b) => a.last_logged_in_at === b.last_logged_in_at ? 0 : a.last_logged_in_at < b.last_logged_in_at ? 1 : -1)
        break
    }
    return rows
  }
  const sortedIds = createMemo(() => {
    const rows = getSortedRows(sort())
    return rows.map(e => e.id)
  })
  function getRow(id) {
    for (const row of (result().rows ?? [])) {
      if (row.id === id) {
        return row
      }
    }
  }

  const allSelected = createMemo(() => {
    const sl = selected().length
    const rl = (result().rows ?? []).length
    return sl && sl === rl
  })

  const today = dayjs().format('YYYY-MM-DD')
  const [loading, setLoading] = createSignal(true)
  const [sending, setSending] = createSignal(false)

  const homeLink = () => {
    const query = new URLSearchParams(document.location.search)
    query.delete('lt')
    query.delete('gt')
    return `${document.location.pathname}?${query.toString()}`
  }
  const prevLink = createMemo(() => paginator.prevLink(result()))
  const nextLink = createMemo(() => paginator.nextLink(result()))
  const lastLink = createMemo(() => paginator.lastLink(result()))
  const hasPaginator = createMemo(() => prevLink() || nextLink())

  onMount(() => {
    fetch(`/api/agents?${query.toString()}`).then(res2json).then(r => { setResult(r.data ?? {}) }).finally(() => { setLoading(false) })
  })

  function toggleAllSelection(val) {
    setSelected(val ? (result().rows ?? []).map(e => e.id) : [])
  }
  function toggleSelection(row) {
    setSelected(e => {
      const i = e.indexOf(row.id)
      if (i > -1) {
        e.splice(i, 1)
        return [...e]
      }
      return [...new Set([...e, row.id])]
    })
  }

  function openAgentInfoDialog(agent) {
    setEditAgent(agent)
    window.agentInfo.showModal()
  }

  function openEditAgentDialog(agent) {
    setEditAgent(agent)
    window.editAgent.showModal()
  }

  function openAgentDaysDialog(agent) {
    setEditAgent(agent)
    window.agentDays.showModal()
  }

  async function batchOp(action) {
    setSending(true)
    fetch(`/api/agents/batch-${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selected() })
    }).then(res2json).then(r => {
      document.location.reload()
    }).finally(() => { setSending(false) })
  }

  async function deleteAgent(agent) {
    if (confirm(`您确定要删除代理”${agent.username}“吗?`)) {
      return fetch(`/api/agents/${agent.id}`, {
        method: 'DELETE'
      }).then(res2json).then(() => {
        document.location.reload()
      })
    }
  }

  function ActionPanel() {
    return (
      <>
        {!selected().length ? (<></>) : (
          <div class="sticky bottom-0 flex items-center gap-4 bg-base-100">
            <button type="button" onClick={() => batchOp('enable')} class="btn" disabled={sending()}>启用</button>
            <button type="button" onClick={() => batchOp('disable')} class="btn" disabled={sending()}>禁用</button>
          </div>
        )}
      </>
    )
  }

  function Sortable({ field, title }) {
    return (
      <div onClick={() => setSort(e => e === `${field}-down` ? `${field}-up` : `${field}-down`)} class="flex items-center gap-1 select-none cursor-pointer">
        <span>{title}</span>
        {sort() === `${field}-up` && <div class="w-3 h-3"><ArrowUpOutline /></div>}
        {sort() === `${field}-down` && <div class="w-3 h-3"><ArrowDownOutline /></div>}
      </div>
    )
  }

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>代理列表</li>
          </ul>
        </div>
        <div class="flex items-center justify-between">
          <h2 class="text-xl">代理列表</h2>
          <button type="button" onClick={() => window.createAgent.showModal()} class="btn btn-success text-white"><span class="w-5"><AddOutline /></span>开代理</button>
        </div>
        <form class="flex flex-wrap items-center gap-2">
          <select name="game" value={game()} onChange={e => setGame(e.target.value)} class="select select-bordered">
            <option value="">全部游戏</option>
            {Object.keys(games.names).map(e => (<option value={e}>{games.names[e]}({e})</option>))}
          </select>
          <div class="join">
            <input type="text" name="k" value={keywords()} onChange={e => setKeywords(e.target.value)} class="input input-bordered join-item" placeholder="用户名" />
            <button type="submit" class="btn join-item"><span class="w-5"><SearchOutline /></span></button>
          </div>
        </form>
        <div class="overflow-x-auto">
          <table class="table whitespace-nowrap">
            <thead>
              <tr class="bg-base-200">
                <th class="px-2 lg:px-3"><input type="checkbox" class="checkbox" checked={allSelected()} onChange={e => toggleAllSelection(e.target.checked)} /></th>
                <th class="px-2 lg:px-3"><Sortable field="status" title="状态" /></th>
                <th class="px-2 lg:px-3"><Sortable field="username" title="用户名(昵称)" /></th>
                <th class="px-2 lg:px-3"><Sortable field="expires" title="可用天数" /></th>
                <th class="px-2 lg:px-3">网址</th>
                <th class="px-2 lg:px-3"><Sortable field="game" title="游戏" /></th>
                <th class="px-2 lg:px-3">玩法</th>
                <th class="px-2 lg:px-3">网盘</th>
                <th class="px-2 lg:px-3"><Sortable field="created" title="创建时间" /></th>
                <th class="px-2 lg:px-3"><Sortable field="login" title="最近登陆时间" /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <For each={sortedIds()}>
                {(id) => {
                  const row = getRow(id)
                  return (
                    <tr class="hover:bg-base-200">
                      <td class="px-2 lg:px-3"><input type="checkbox" class="checkbox" checked={selected().indexOf(row.id) > -1} onChange={e => { toggleSelection(row) }} /></td>
                      <td class={row.is_disabled ? 'px-2 lg:px-3 text-error' : 'px-2 lg:px-3 text-success'}><div class="w-6">{row.is_disabled ? <CloseCircleOutline /> : <CheckmarkCircleOutline />}</div></td>
                      <td class="px-2 lg:px-3">
                        <div class="tooltip" data-tip="编辑">
                          <button type="button" onClick={() => openEditAgentDialog(row)}><span class="text-blue-500">{row.username}</span><span class="text-gray-500">({row.nickname})</span></button>
                        </div>
                      </td>
                      <td class="px-2 lg:px-3">
                        <div class="flex items-center justify-end">
                          <div class="tooltip" data-tip="调整可用天数">
                            <button type="button" onClick={() => openAgentDaysDialog(row)} class={expiresDays(row.games[0].expires_at) <= 0 ? 'text-error' : 'text-blue-500'}>{expiresDays(row.games[0].expires_at)}天</button>
                          </div>
                        </div>
                      </td>
                      <td class="px-2 lg:px-3">
                        <div class="tooltip" data-tip="查看代理后台信息">
                          <button type="button" onClick={() => openAgentInfoDialog(row)} class="btn btn-ghost btn-sm"><span class="w-5"><EyeOutline /></span></button>
                        </div>
                      </td>
                      <td class="px-2 lg:px-3">{games.names[row.games[0].game]}<span class="text-gray-500">({row.games[0].game})</span></td>
                      <td class="px-2 lg:px-3">{row.games[0].methods.map(e => games.methods[e]).join(',')}</td>
                      <td class="px-2 lg:px-3">{row.games[0].site ? `${row.games[0].site.title}: ${row.games[0].site.username}` : '-'}</td>
                      <td class="px-2 lg:px-3 text-right">{row.created_at ? dayjs(row.created_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                      <td class="px-2 lg:px-3 text-right">{row.last_logged_in_at ? dayjs(row.last_logged_in_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                      <td>
                        <div class="flex items-center justify-end">
                          <div class="tooltip" data-tip="删除">
                            <button type="button" onClick={() => deleteAgent(row)} class="btn btn-ghost btn-sm"><span class="w-5"><TrashOutline /></span></button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                }}
              </For>
            </tbody>
          </table>
        </div>
        <ActionPanel />
        <div class="flex items-center justify-end gap-4">
          {hasPaginator() && (
            <div class="join">
              <button type="button" onClick={() => document.location.href = homeLink()} class="btn join-item font-normal" disabled={!prevLink()}>首页</button>
              <button type="button" onClick={() => document.location.href = prevLink()} class="btn join-item font-normal" disabled={!prevLink()}>上一页</button>
              <button type="button" onClick={() => document.location.href = nextLink()} class="btn join-item font-normal" disabled={!nextLink()}>下一页</button>
              <button type="button" onClick={() => document.location.href = lastLink()} class="btn join-item font-normal" disabled={!nextLink()}>尾页</button>
            </div>
          )}
          <div>总计 {result().total ?? 0} 笔</div>
        </div>
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}

        <AgentInfoDialog agent={editAgent} />
        <CreateAgentDialog />
        <EditAgentDialog agent={editAgent} />
        <AgentDaysDialog agent={editAgent} />
      </Portal>
    </>
  )
}

function randomUsername() {
  const prefix = String.fromCharCode(...Array(3).fill(0).map(() => Math.floor(Math.random() * 26) + 97))
  const suffix = String.fromCharCode(...Array(4).fill(0).map(() => Math.floor(Math.random() * 10) + 48))
  return `${prefix}${suffix}`
}

const passwordRange = '+-0123456789=@#ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
function randomPassword() {
  return Array(8).fill(0).map(() => passwordRange[Math.floor(Math.random() * passwordRange.length)]).join('')
}

function CreateAgentDialog() {
  const [username, setUsername] = createSignal('')
  const [nickname, setNickname] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [game, setGame] = createSignal('T5')
  const [days, setDays] = createSignal(30)
  const [methods, setMethods] = createSignal(['fixed'])

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setUsername(randomUsername())
          setNickname('')
          setPassword(randomPassword())
          setGame('T5')
          setDays(30)
          setMethods(['fixed'])

          setErrors({})
        })
      }
    }).observe(window.createAgent, { attributes: true })
  })

  const gamePlays = createMemo(() => games.plays[game()])

  createEffect(() => {
    const val = game()
    const plays = games.plays[val]
    setMethods([plays[0]])
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch('/api/agents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username().trim(),
        nickname: nickname().trim(),
        password: password(),
        game: game(),
        days: Number(days()),
        methods: methods()
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.createAgent.close()
  }

  function toggleMethods(method, on) {
    setMethods(e => {
      const i = e.indexOf(method)
      if (on && i === -1) {
        const r = [...e, method]
        r.sort()
        return r
      }
      if (!on && i > -1) {
        e.splice(i, 1)
        return [...e]
      }
      return e
    })
  }

  return (
    <dialog id="createAgent" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">开代理</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={username()} onChange={e => setUsername(e.target.value)} class="grow" placeholder="请输入玩家用户名" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setUsername(randomUsername())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().username && (<div class="label"><span class="label-text-alt text-error">{errors().username}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <input type="text" value={nickname()} onChange={e => setNickname(e.target.value)} class="input input-bordered" placeholder="请输入昵称" required />
          {errors().nickname && (<div class="label"><span class="label-text-alt text-error">{errors().nickname}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">登陆密码</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={password()} onChange={e => setPassword(e.target.value)} class="grow" placeholder="请输入玩家登陆密码" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setPassword(randomPassword())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">开通游戏</span></div>
          <select name="game" value={game()} onChange={e => setGame(e.target.value)} class="select select-bordered" required>
            <option value="" selected disabled>请选择要开通的游戏</option>
            {Object.entries(games.names).map(([k, v]) => (<option value={k}>{v}({k})</option>))}
          </select>
          {errors().game && (<div class="label"><span class="label-text-alt text-error">{errors().game}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">游戏玩法</span></div>
          <div class="flex items-center gap-8">
            {gamePlays().indexOf('fixed') > -1 && (
              <label class="label cursor-pointer w-1/2 justify-start gap-2">
                <input type="checkbox" checked={methods().indexOf('fixed') > -1} onChange={e => toggleMethods('fixed', e.target.checked)} class="checkbox" />
                <span class="label-text">定位</span>
              </label>
            )}
            {gamePlays().indexOf('fan') > -1 && (
              <label class="label cursor-pointer w-1/2 justify-start gap-2">
                <input type="checkbox" checked={methods().indexOf('fan') > -1} onChange={e => toggleMethods('fan', e.target.checked)} class="checkbox" />
                <span class="label-text">番摊</span>
              </label>
            )}
          </div>
          {errors().methods && (<div class="label"><span class="label-text-alt text-error">{errors().methods}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">可用天数</span></div>
          <input type="number" value={days()} onChange={e => setDays(e.target.value)} class="input input-bordered" step="1" min="1" max={store.score} required />
          {errors().days && (<div class="label"><span class="label-text-alt text-error">{errors().days}</span></div>)}
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}

function EditAgentDialog({ agent }) {
  const [nickname, setNickname] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [game, setGame] = createSignal('T5')
  const [isDisabled, setIsDisabled] = createSignal(false)
  const [methods, setMethods] = createSignal(['fixed'])

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setNickname(agent().nickname)
          setPassword('')
          setGame(agent().games[0].game)
          setIsDisabled(agent().is_disabled)
          setMethods(agent().games[0].methods ?? [games.plays[game()][0]])

          setErrors({})
        })
      }
    }).observe(window.editAgent, { attributes: true })
  })

  const gamePlays = createMemo(() => games.plays[game()])

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch(`/api/agents/${agent().id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nickname: nickname().trim(),
        password: password(),
        game: game(),
        is_disabled: !!isDisabled(),
        methods: methods()
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.editAgent.close()
  }

  function toggleMethods(method, on) {
    setMethods(e => {
      const i = e.indexOf(method)
      if (on && i === -1) {
        const r = [...e, method]
        r.sort()
        return r
      }
      if (!on && i > -1) {
        e.splice(i, 1)
        return [...e]
      }
      return e
    })
  }

  return (
    <dialog id="editAgent" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">编辑代理</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{agent().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <input type="text" value={nickname()} onChange={e => setNickname(e.target.value)} class="input input-bordered" placeholder="请输入昵称" required />
          {errors().nickname && (<div class="label"><span class="label-text-alt text-error">{errors().nickname}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">修改密码</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={password()} onChange={e => setPassword(e.target.value)} class="grow" placeholder="不修改请留空" />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setPassword(randomPassword())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">开通游戏</span></div>
          {(agent().games ?? []).length > 0 && <div class="input input-bordered flex items-center">{games.names[agent().games[0].game]}</div>}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">游戏玩法</span></div>
          <div class="flex items-center gap-8">
            {gamePlays().indexOf('fixed') > -1 && (
              <label class="label cursor-pointer w-1/2 justify-start gap-2">
                <input type="checkbox" checked={methods().indexOf('fixed') > -1} onChange={e => toggleMethods('fixed', e.target.checked)} class="checkbox" />
                <span class="label-text">定位</span>
              </label>
            )}
            {gamePlays().indexOf('fan') > -1 && (
              <label class="label cursor-pointer w-1/2 justify-start gap-2">
                <input type="checkbox" checked={methods().indexOf('fan') > -1} onChange={e => toggleMethods('fan', e.target.checked)} class="checkbox" />
                <span class="label-text">番摊</span>
              </label>
            )}
          </div>
          {errors().methods && (<div class="label"><span class="label-text-alt text-error">{errors().methods}</span></div>)}
        </div>
        <div>
          <label class="label cursor-pointer inline-flex gap-2">
            <input type="checkbox" onChange={e => setIsDisabled(!e.target.checked)} class="checkbox" checked={!isDisabled()} />
            <span class="label-text">启用账号</span>
          </label>
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}

function AgentInfoDialog({ agent }) {
  const [url, setUrl] = createSignal('')

  let elQrcode

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        setUrl(`${import.meta.env.VITE_AGENT_URL_PREFIX}/login/${agent().games[0].code}`)
        elQrcode.innerHTML = ''
        new window.QRCode(elQrcode, { text: url(), width: 256, height: 256, correctLevel: window.QRCode.CorrectLevel.H })
      }
    }).observe(window.agentInfo, { attributes: true })
  })

  function copyToClipboard() {
    navigator.clipboard.writeText(url())
    addToast({ message: '已复制到剪贴板' })
  }

  return (
    <dialog id="agentInfo" class="modal">
      <div class="modal-box flex flex-col gap-4">
        <form method="dialog">
          <button type="submit" class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
        </form>
        <h3 class="text-lg">代理后台信息</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{agent().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">游戏</span></div>
          <div class="input input-bordered flex items-center">{games.names[(agent().games ?? [])[0]?.game]}({(agent().games ?? [])[0]?.game})</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">网址</span></div>
          <div class="textarea textarea-bordered flex items-center gap-2">
            <div class="grow break-all select-all text-base">{url()}</div>
            <div class="tooltip" data-tip="复制">
              <button type="button" onClick={() => copyToClipboard()} class="btn btn-ghost btn-sm"><span class="w-6"><CopyLine /></span></button>
            </div>
          </div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">网址二维码</span></div>
          <div ref={elQrcode} class="textarea textarea-bordered flex justify-center"></div>
        </div>
        <div class="modal-action">
          <form method="dialog">
            <button type="submit" class="btn px-8">关闭</button>
          </form>
        </div>
      </div>
    </dialog>
  )
}

export function AgentDaysDialog({ agent }) {
  const [days, setDays] = createSignal(0)
  const [action, setAction] = createSignal('grant')

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  const daysVal = createMemo(() => (agent().games ?? [])[0]?.expires_at ? expiresDays(agent().games[0].expires_at) : 0)

  let elFocus

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setDays(0)
          setAction('grant')
          setErrors({})
        })
        elFocus.focus()
      }
    }).observe(window.agentDays, { attributes: true })
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch(`/api/agents/${agent().id}/days/${action()}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        days: Number(days()),
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.agentDays.close()
  }

  return (
    <dialog id="agentDays" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">调整可用天数</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{agent().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">{agent().nickname}</div>
        </div>
        <div class="form-control">
          <div class="label">
            <span class="label-text">剩余天数</span>
            {daysVal() > 0 && <span class="label-text-alt">到期日期: {dayjs(agent().games[0].expires_at * 1000).tz(constants.timeZone).format('YYYY-MM-DD HH:mm:ss')}</span>}
          </div>
          <div class="input input-bordered flex items-center">{daysVal()}</div>
        </div>
        <div class="form-control">
          <div class="flex items-center gap-4">
            <label class="label cursor-pointer gap-2">
              <input type="radio" name="action" value="grant" onChange={e => setAction(e.target.value)} class="radio" checked={action() === 'grant'} />
              <span class="label-text">增加</span>
            </label>
            <label class="label cursor-pointer gap-2">
              <input type="radio" name="action" value="revoke" onChange={e => setAction(e.target.value)} class="radio" checked={action() === 'revoke'} />
              <span class="label-text">减少</span>
            </label>
          </div>
        </div>
        <div class="form-control">
          <div class="label">
            <span class="label-text">{action() === 'grant' ? '增加' : '减少'}天数</span>
            {action() === 'grant' && <span class="label-text-alt">可增加天数: {store.score}</span>}
            {action() === 'revoke' && <span class="label-text-alt">可减少天数: {daysVal()}</span>}
          </div>
          <input ref={elFocus} type="number" name="score" value={days()} onChange={e => setDays(e.target.value)} class="input input-bordered" min="0" max={action() === 'grant' ? store.score : daysVal()} required />
          {errors().days && (<div class="label"><span class="label-text-alt text-error">{errors().days}</span></div>)}
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}
